import { formatPesoAmount } from '../utils/money';
import { calculateTemporaryPriceLine, resolveTemporaryUnitPrice } from '../utils/temporaryPriceCore';
import { buildLineKey } from '../utils/documentLineItems';
import { getClientCatalogPricingFields } from '../utils/clientCatalogPricing';
import React from 'react';
import Barcode from './Barcode';
import ScaleWeighingSummary from './ScaleWeighingSummary';
import { FiInfo } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import { normalizeQuantity } from '../utils/quantity';
import { isScaleInputPrice } from '../utils/scalePresentation';
import { getProductFormatLabel, normalizeProductFormatCode, resolvePriceProductFormatCode } from '../utils/clientType';
import { packWeightLabel } from '../utils/varietyFormatIdentity';
import { comparePosPriceFormats } from '../utils/posFormatFilters';
import {
  formatPromotionDate,
  formatPromotionTerms,
  getPromotionState,
  normalizeFormatPromotions,
} from '../utils/priceListPromotions';

const truncateDescription = (value, maxLength = 80) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

const formatMoney = (value) => `$${formatPesoAmount(Number(value || 0))}`;

const formatQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const resolveVarietyDescription = (variety) =>
  String(variety?.description || '').trim();

const isSameScaleSelection = (selection, varietyId, priceIndex, price) => {
  if (!selection) return false;
  if (Number(selection.varietyId) !== Number(varietyId)) return false;
  if (Number.isInteger(selection.priceIndex) && Number(selection.priceIndex) === Number(priceIndex)) {
    return true;
  }
  return selection.varietyPriceId != null && Number(selection.varietyPriceId) === Number(price?.id);
};

const VarietyDetailModal = ({
  variety,
  onClose,
  handleClick,
  allowSoldOutSelection = false,
  priceFilter,
  formatOptions,
  sortByFormat = false,
  resolveDisplayPrice,
  scalePricingPreview = null,
  quantities = {},
  discounts = {},
  linePriceMeta = {},
  activeScaleSelection = null,
  scaleWeightKg = null,
  onPriceIncrement,
  onPriceDecrement,
  onPriceKeypad,
  onBackToVarieties,
  onConfirmClose,
  showPrices = true,
  showBarcode = showPrices,
  showScaleSummary = true,
  overlayZClassName = 'z-[1600]',
  promotionNow = Date.now(),
}) => {
  const [selectedPriceKey, setSelectedPriceKey] = React.useState(null);
  const contentRef = React.useRef(null);
  React.useEffect(() => {
    setSelectedPriceKey(null);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [variety?.id]);

  const getPriceSelectionKey = (price, priceIndex) => (
    `${variety?.id ?? 'variety'}:${price?.id ?? priceIndex ?? 'price'}:${priceIndex ?? ''}`
  );
  const hasCardActions = (
    typeof onPriceIncrement === 'function'
    || typeof onPriceDecrement === 'function'
    || typeof onPriceKeypad === 'function'
  );
  const actClick = (price, priceIndex, isDisabled) => {
    if (isDisabled) return;
    setSelectedPriceKey(getPriceSelectionKey(price, priceIndex));
    if (handleClick) {
      handleClick(price);
    }
  };
  const sellablePrices = (variety.VarietyPrices || [])
    .map((price, priceIndex) => ({ price, priceIndex }))
    .filter(({ price }) => (
      price && (showPrices ? price.price !== null && price.price !== undefined : true)
    ));
  const eligiblePrices = sellablePrices.filter(({ price }) =>
    priceFilter ? priceFilter(price) : true
  );
  const compactLayout = Array.isArray(formatOptions);
  const formatLabels = new Map((formatOptions || []).map((option) => (
    [normalizeProductFormatCode(option.value), option.label]
  )));
  const pricedEntries = eligiblePrices.map(({ price: catalogPrice, priceIndex }) => {
    const current = normalizeFormatPromotions(catalogPrice)
      .filter((promotion) => getPromotionState(promotion, promotionNow) === 'active');
    const temporary = resolveTemporaryUnitPrice(catalogPrice.regularPrice ?? catalogPrice.price, current, promotionNow);
    const price = temporary.effectivePrice === catalogPrice.price ? catalogPrice
      : { ...catalogPrice, regularPrice: temporary.regularPrice, price: temporary.effectivePrice };
    const activePromotions = current.filter((promotion) => ['pack', 'weight_threshold'].includes(promotion.type) || promotion.id === temporary.unitPrice?.id);
    const basePrice = Math.max(0, Number(price.price || 0));
    const matrixDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
    const priceDiscount = Number(price.discount || 0);
    const unitDiscount = Math.min(
      Math.max(matrixDiscount > 0 ? matrixDiscount : priceDiscount, 0),
      basePrice
    );
    const baseDisplay = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, 0)
      : null;
    const display = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, unitDiscount)
      : null;
    const shownBasePrice = Number.isFinite(Number(baseDisplay?.price))
      ? Number(baseDisplay.price)
      : basePrice;
    const ordinaryShownPrice = Number.isFinite(Number(display?.price))
      ? Number(display.price)
      : Number.isFinite(Number(price.finalPrice)) && unitDiscount > 0
        ? Number(price.finalPrice)
        : Math.max(basePrice - unitDiscount, 0);
    const shownPrice = ordinaryShownPrice;
    const meta = linePriceMeta[buildLineKey(variety.id, priceIndex)] || {};
    const line = calculateTemporaryPriceLine({
      ...price, ...getClientCatalogPricingFields(price), ...meta,
      varietyPriceId: price.id,
      quantity: normalizeQuantity(quantities?.[variety.id]?.[priceIndex] || 0),
      discount: unitDiscount,
      discountSource: meta.discountSource || price.discountSource || (unitDiscount > 0 ? 'client_category' : 'none'),
      manualDiscount: meta.manualDiscountCandidate ?? (meta.discountSource === 'manual' ? unitDiscount : 0),
    }, activePromotions, promotionNow);
    const subtotalDisplay = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, { ...price, price: line.subtotal }, 0) : null;
    return {
      price,
      priceIndex,
      shownPrice,
      shownBasePrice,
      ordinaryShownPrice,
      unitDiscount,
      discountSource: price.discountSource || (unitDiscount > 0 ? 'client_category' : 'none'),
      discountType: price.discountType || null,
      discountValue: price.discountValue || 0,
      clientPricingCategoryName: price.clientPricingCategoryName || '',
      taxFreeApplied: Boolean(baseDisplay?.taxFreeApplied || display?.taxFreeApplied),
      quantity: normalizeQuantity(quantities?.[variety?.id]?.[priceIndex] || 0),
      activePromotions,
      temporarySubtotal: Number.isFinite(Number(subtotalDisplay?.price)) ? Number(subtotalDisplay.price) : line.subtotal,
    };
  });
  const sortedPricedEntries = pricedEntries.slice().sort((entryA, entryB) => (
    (sortByFormat ? comparePosPriceFormats(entryA.price, entryB.price, formatOptions) : 0)
    || (showPrices ? entryB.shownPrice - entryA.shownPrice : 0)
  ));
  const varietyDescription = resolveVarietyDescription(variety);
  const isVarietySoldOut = eligiblePrices.length > 0 && eligiblePrices.every(({ price }) => price.isSoldOut);
  const hasScalePrices = showScaleSummary && pricedEntries.some(({ price }) => (
    isScaleInputPrice(price)
  ));
  const activeScaleEntry = pricedEntries.find(({ price, priceIndex }) => (
    isScaleInputPrice(price)
    && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price)
  ));
  const scaleSummaryWeightKg = Number(scaleWeightKg);
  const matchingScalePreview = activeScaleEntry
    && Number(scalePricingPreview?.varietyPriceId) === Number(activeScaleEntry.price.id)
    && Number(scalePricingPreview?.weightKg) === scaleSummaryWeightKg ? scalePricingPreview : null;
  const scaleSummaryUnitPrice = matchingScalePreview?.unitPrice ?? (activeScaleEntry ? activeScaleEntry.shownPrice : null);
  const selectedPriceEntry = pricedEntries.find(({ price, priceIndex }) => (
    selectedPriceKey === getPriceSelectionKey(price, priceIndex)
  ));
  const selectedPriceIsScale = showScaleSummary && isScaleInputPrice(selectedPriceEntry?.price);
  const summaryMode = showPrices && selectedPriceEntry && !selectedPriceIsScale
    ? 'quantity'
    : showPrices && hasScalePrices
      ? 'scale'
      : null;
  const displaySummaryMode = summaryMode || (showPrices ? 'quantity' : null);
  const isEmptyQuantitySummary = !summaryMode && displaySummaryMode === 'quantity';
  const summaryThemeClass = displaySummaryMode === 'quantity'
    ? 'border-blue-100 bg-blue-50/80 text-blue-950'
    : 'border-emerald-100 bg-emerald-50/80 text-emerald-950';
  const summaryTitleClass = displaySummaryMode === 'quantity'
    ? 'text-blue-700'
    : 'text-emerald-700';
  const selectedQuantity = selectedPriceEntry?.quantity ?? 0;
  const hasValidScaleSummaryWeight = scaleWeightKg != null && Number.isFinite(scaleSummaryWeightKg);
  const summaryWeight = displaySummaryMode === 'quantity'
    ? (isEmptyQuantitySummary ? null : selectedQuantity)
    : (hasValidScaleSummaryWeight ? scaleSummaryWeightKg : null);
  const summaryUnitPrice = displaySummaryMode === 'quantity'
    ? selectedQuantity > 0 && selectedPriceEntry?.activePromotions.length
      ? selectedPriceEntry.temporarySubtotal / selectedQuantity : selectedPriceEntry?.shownPrice
    : scaleSummaryUnitPrice;
  const summaryAmountTitle = displaySummaryMode === 'quantity' ? 'Cantidad' : 'Peso';
  const summaryAmountLabel = displaySummaryMode === 'quantity'
    ? (isEmptyQuantitySummary ? '' : formatQuantity(selectedQuantity))
    : undefined;

  const renderPriceCard = (entry) => {
    const {
      price,
      priceIndex,
      shownPrice,
      shownBasePrice,
      ordinaryShownPrice,
      unitDiscount,
      discountType,
      discountValue,
      clientPricingCategoryName,
      taxFreeApplied,
      quantity,
      activePromotions,
      temporarySubtotal,
    } = entry;
    const formatCode = resolvePriceProductFormatCode(price);
    const formatLabel = formatLabels.get(formatCode)
      || price.productFormatName || price.productFormat?.name || price.ProductFormat?.name
      || getProductFormatLabel(formatCode);
    const priceKey = getPriceSelectionKey(price, priceIndex);
    const isDisabled = price.isSoldOut && !allowSoldOutSelection;
    const isScalePrice = isScaleInputPrice(price);
    const isSelectedScalePrice = isScalePrice && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price);
    const isSelectedPrice = selectedPriceKey === priceKey;
    const hasCartQuantity = normalizeQuantity(quantity) > 0;
    const quantityLabel = formatQuantity(quantity);
    const ordinarySubtotal = Math.floor(ordinaryShownPrice * quantity);
    const subtotal = activePromotions.length ? temporarySubtotal : ordinarySubtotal;
    const description = truncateDescription(price.description || varietyDescription) || '';
    const discountAmountLabel = formatMoney(Math.max(shownBasePrice - shownPrice, 0));
    const hasUnitDiscount = showPrices && unitDiscount > 0 && shownBasePrice > shownPrice;
    const discountValueLabel = discountType === 'percent' && Number(discountValue) > 0
      ? ` (${Number(discountValue).toLocaleString('de-DE')}%)`
      : '';
    const handleCardAction = (callback) => {
      if (isDisabled) return;
      setSelectedPriceKey(priceKey);
      callback?.(price, priceIndex);
    };

    return (
      <article
        key={price?.id || priceIndex}
        aria-disabled={isDisabled}
        className={`relative overflow-hidden rounded-xl border bg-white shadow-sm transition ${
          isDisabled
            ? 'cursor-not-allowed border-gray-200 opacity-70'
            : isSelectedScalePrice
              ? 'border-emerald-300 shadow-md ring-2 ring-emerald-100'
              : isSelectedPrice
                ? 'border-blue-300 shadow-md ring-2 ring-blue-100'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
        }`}
      >
        {hasCardActions ? (
          <div className="absolute inset-0 grid grid-cols-2">
            <button
              type="button"
              onClick={() => handleCardAction(onPriceDecrement)}
              disabled={isDisabled}
              className={`min-h-full border-r border-transparent transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-red-50/0 hover:bg-red-50/70'
              }`}
              title="Click a la izquierda para restar"
              aria-label={`Restar ${price.metricUnit || 'precio'}`}
            />
            <button
              type="button"
              onClick={() => handleCardAction(onPriceIncrement)}
              disabled={isDisabled}
              className={`min-h-full transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-emerald-50/0 hover:bg-emerald-50/70'
              }`}
              title="Click a la derecha para sumar"
              aria-label={`Sumar ${price.metricUnit || 'precio'}`}
            />
          </div>
        ) : (
          <button
            type="button"
            aria-label={`Seleccionar ${price.metricUnit || 'precio'}`}
            onClick={() => actClick(price, priceIndex, isDisabled)}
            disabled={isDisabled}
            className="absolute inset-0"
          />
        )}

        {price.isSoldOut && (
          <span className="absolute right-2 top-2 z-20 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
            Agotado
          </span>
        )}
        {isSelectedScalePrice && (
          <span className="absolute left-1/2 top-2 z-20 -translate-x-1/2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
            Seleccionado
          </span>
        )}

        <div className="relative z-10 pointer-events-none p-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="relative shrink-0">
              <img
                src={price.priceImage || variety.varietyImage || 'https://placehold.co/100'}
                alt={variety.varietyName}
                className="h-14 w-14 rounded-lg object-cover shadow"
              />
              {hasCartQuantity && (
                <span
                  className="absolute -right-2 -top-2 flex min-h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1.5 text-xs font-black leading-none text-white shadow"
                  title={`Cantidad: ${quantityLabel}`}
                  aria-label={`Cantidad: ${quantityLabel}`}
                >
                  {quantityLabel}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              {compactLayout && formatLabel && (
                <span
                  aria-label={`Formato: ${formatLabel}`}
                  className="mb-1 inline-block max-w-full rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 [overflow-wrap:anywhere]"
                >
                  {formatLabel}
                </span>
              )}
              <p className="text-sm font-medium text-gray-700">{price.metricUnit}{packWeightLabel(price) && ` · ${packWeightLabel(price)}`}</p>
              {showPrices ? (
                <div>
                  {hasUnitDiscount && (
                    <span className="block text-xs font-semibold text-gray-400 line-through">
                      {formatMoney(shownBasePrice)}
                    </span>
                  )}
                  <span className="inline-flex items-center text-lg font-bold text-gray-900">
                    {formatMoney(shownPrice)}
                    {taxFreeApplied && (
                      <span
                        className="ml-1 inline-flex text-blue-700"
                        title="Precio ajustado sin impuestos excluidos."
                        aria-label="Precio ajustado sin impuestos excluidos."
                      >
                        <FiInfo size={13} />
                      </span>
                    )}
                  </span>
                </div>
              ) : (
                <span className="inline-flex items-center text-sm font-semibold text-blue-700">
                  Formato disponible
                </span>
              )}
            </div>
            {hasCardActions && (
              <button
                type="button"
                onClick={() => handleCardAction(onPriceKeypad)}
                disabled={isDisabled}
                className={`pointer-events-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title="Cantidad exacta"
                aria-label="Cantidad exacta"
              >
                <FaCalculator size={16} />
              </button>
            )}
          </div>

          <p className="mt-3 min-h-[2.5rem] break-all text-sm leading-snug text-gray-600">
            {description || '\u200e'}
          </p>

          {hasUnitDiscount && (
            <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
              Descuento{clientPricingCategoryName ? ` ${clientPricingCategoryName}` : ''}:<br/> -{discountAmountLabel}{discountValueLabel}
            </div>
          )}

          {showPrices && activePromotions.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {activePromotions.map((promotion) => (
                <div
                  key={promotion.id}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-900"
                >
                  <span className="block">Temporal: {formatPromotionTerms(promotion)}</span>
                  {promotion.endDate && (
                    <span className="block font-medium text-amber-700">
                      Vigente hasta el {formatPromotionDate(promotion.endDate)} inclusive
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {showPrices && (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              <div className="text-sm font-bold text-gray-900">
                Subtotal: {formatMoney(subtotal)}
              </div>
            </div>
          )}

          {showBarcode && (
            price.barcode ? (
              <Barcode value={price.barcode} className="mt-3" />
            ) : (
              <p className="mt-3 text-center text-xs text-gray-500">
                Sin codigo de barras
              </p>
            )
          )}

        </div>
      </article>
    );
  };

  return (
    <div
      className={`fixed inset-0 ${overlayZClassName} flex items-center justify-center overflow-y-auto bg-black/50 px-3 py-4 sm:px-4 sm:py-6`}
      style={{
        paddingTop: 'calc(1rem + env(safe-area-inset-top))',
        paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
        style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 sm:px-6 ${compactLayout ? 'py-3' : 'py-4'}`}>
          <div className="flex min-w-0 items-start gap-3">
            <div className={`relative shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 ${compactLayout ? 'h-10 w-10' : 'h-14 w-14 lg:hidden'}`}>
              <img
                src={variety.varietyImage || 'https://placehold.co/400'}
                alt={variety.varietyName}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              {!compactLayout && <p className="text-xs uppercase tracking-wide text-gray-500">Variedad</p>}
              <h2 className={`break-words font-semibold text-gray-900 ${compactLayout ? 'text-base leading-snug' : 'text-lg'}`}>{variety.varietyName}</h2>
              {varietyDescription && (
                <p
                  className={compactLayout ? 'mt-0.5 truncate text-xs text-gray-600' : 'mt-1 text-sm leading-snug text-gray-600 lg:hidden'}
                  title={varietyDescription}
                >
                  {compactLayout ? varietyDescription : truncateDescription(varietyDescription, 120)}
                </p>
              )}
              {isVarietySoldOut && (
                <span className="mt-1 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                  AGOTADO
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
          >
            X
          </button>
        </div>

        <div ref={contentRef} className={`grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto p-4 sm:p-6 ${compactLayout ? '' : 'sm:gap-6 lg:grid-cols-[240px_1fr]'}`}>
          {!compactLayout && (
            <div className="hidden space-y-3 sm:space-y-4 lg:block">
              <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
                <div className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Imagen</div>
                <div className="relative mx-auto w-20 h-20 sm:w-28 sm:h-28 lg:w-full lg:aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                  <img
                    src={variety.varietyImage || 'https://placehold.co/400'}
                    alt={variety.varietyName}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
              {variety.varietyName && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="text-xs font-semibold text-gray-600 uppercase">Nombre</div>
                  <p className="mt-2 text-sm text-gray-700">{variety.varietyName}</p>
                </div>
              )}
              {varietyDescription && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="text-xs font-semibold text-gray-600 uppercase">Descripcion</div>
                  <p
                    className="mt-2 text-sm text-gray-700 break-words"
                    title={varietyDescription}
                  >
                    {truncateDescription(varietyDescription, 140)}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className={`min-w-0 ${compactLayout ? 'space-y-3' : 'space-y-4'}`}>
            <div>
              <h3 className={`font-semibold text-gray-900 ${compactLayout ? 'text-sm' : 'text-lg'}`}>
                {showPrices ? 'Precios disponibles' : 'Formatos disponibles'}
              </h3>
            </div>
            {displaySummaryMode && (
              <div className={`border ${compactLayout ? 'rounded-xl p-2' : 'rounded-2xl p-3 shadow-sm'} ${summaryThemeClass}`}>
                <div className={`text-xs font-semibold uppercase tracking-wide ${summaryTitleClass}`}>
                  {displaySummaryMode === 'quantity' ? 'Precio seleccionado' : 'Lectura de balanza'}
                </div>
                <ScaleWeighingSummary
                  weightKg={summaryWeight}
                  unitPrice={summaryUnitPrice}
                  subtotal={displaySummaryMode === 'quantity' && !isEmptyQuantitySummary
                    ? selectedPriceEntry?.temporarySubtotal : matchingScalePreview?.subtotal}
                  amountTitle={summaryAmountTitle}
                  amountLabel={summaryAmountLabel}
                  allowZeroAmount
                  emptyValues={isEmptyQuantitySummary}
                  className={compactLayout ? 'mt-2 !grid-cols-3 !gap-2' : 'mt-3 gap-2'}
                  cardClassName={compactLayout ? 'min-w-0 rounded-lg bg-white/75 px-2 py-1.5' : 'rounded-xl bg-white/75 px-3 py-2'}
                  valueClassName={compactLayout ? 'mt-1 text-sm font-bold [overflow-wrap:anywhere]' : undefined}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedPricedEntries.length > 0 ? (
                sortedPricedEntries.map(renderPriceCard)
              ) : (
                <p className="col-span-full text-sm text-gray-500 text-center">
                  {showPrices ? 'Sin precio disponible' : 'Sin formato disponible'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={`flex shrink-0 flex-wrap border-t border-gray-100 bg-white px-4 py-3 sm:px-6 ${
          onBackToVarieties ? 'justify-between gap-3' : 'justify-end'
        }`}
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          {onBackToVarieties && (
            <button
              type="button"
              onClick={onBackToVarieties}
              className="rounded-xl border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              Volver a variedades
            </button>
          )}
          <button
            type="button"
            onClick={onConfirmClose || onClose}
            className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarietyDetailModal;
